import { useEffect, useMemo, useRef, useState } from 'react';
import { useClient } from 'react-fetching-library';
import { listProvidersAction, PROVIDER_DEFAULT_FLAGS } from '../api';
import { PROVIDERS_SORT } from '../lib';

const PICKER_PAGE_SIZE = 100;

// Loads the FULL provider list for an account (all continuation-token
// pages, name-sorted) for picker-style consumers where paging inside a
// dropdown is not practical. The request-id guard drops responses that
// arrive after the account changed or the component unmounted.
// An account has THREE independent defaults, one per context. A caller
// must say which default it means; one that does not ask gets none
// rather than a guess.
export const useProviders = (accountUuid, { skip = false, defaultContext = null } = {}) => {
  const client = useClient();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!accountUuid || skip) return undefined;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      setError(false);
      const all = [];
      let token = null;
      do {
        // eslint-disable-next-line no-await-in-loop
        const response = await client.query(
          listProvidersAction({
            accountUuid,
            sort: PROVIDERS_SORT.nameAsc,
            pageSize: PICKER_PAGE_SIZE,
            continuationToken: token || undefined,
          })
        );
        if (cancelled || requestIdRef.current !== requestId) return;
        if (response.error) {
          setError(true);
          setLoading(false);
          return;
        }
        all.push(...(response.payload?.items || []));
        token = response.payload?.continuationToken || null;
      } while (token);
      setProviders(all);
      setLoading(false);
    };

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [accountUuid, skip, client]);

  const defaultProvider = useMemo(() => {
    const flag = PROVIDER_DEFAULT_FLAGS[defaultContext];
    if (!flag) return null;
    return providers.find(provider => provider[flag]) || null;
  }, [providers, defaultContext]);

  return { providers, defaultProvider, loading, error };
};

export default useProviders;
